// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {ChatInput, type ChatInputProps} from '@github-ui/copilot-chat/components/ChatInput'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {queryForRepos, RepoBranchAgentPicker} from '@github-ui/copilot-chat/components/RepoBranchAgentSelector'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useChatManager} from '@github-ui/copilot-chat/CopilotChatManagerContext'
import {
  ModeSupportName,
  type CopilotChatRepo,
  type LineRange,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/copilot-chat/utils/copilot-chat-types'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useLastUsedRepository} from '@github-ui/copilot-chat/hooks/use-last-used-repository'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {useCallback, useEffect, useState} from 'react'
import {AgentIcon} from '@primer/octicons-react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {Repository} from '@github-ui/current-repository'
import {useSearchParams} from '@github-ui/react-router'
import {MemberRequestBanner} from './MemberRequestBanner'
import type {DropdownRepository} from '../../hooks/useCopilotRepoFetching'
import {ModelPicker} from '../task-view/ModelPicker'
import {useAgentModels} from '../../hooks/useAgentModels'
import {useLatestAgentModel} from '../../hooks/use-latest-agent-model'
import {useAgentInfo} from '../../hooks/use-agent-info'
import {isCopilotOrCustomAgent} from '../../utils/agent-helpers'
import {useLastSelectedAgent} from '../../hooks/use-last-selected-agent'

import {
  makeFileReference,
  makeSnippetReference,
  referenceName as getReferenceName,
  // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {SelectedAgent} from '../../types/session'
import {ToggleSwitch} from '@primer/react'

export type CreateTaskReference = {
  path: string
  range?: LineRange
}

function makeFileOrSnippetReference({
  referenceRepo,
  reference,
}: {
  referenceRepo: CopilotChatRepo
  reference: CreateTaskReference
}) {
  if (reference.range) {
    return makeSnippetReference(reference.path, reference.range, referenceRepo)
  } else {
    return makeFileReference(reference.path, referenceRepo)
  }
}

export type NewTaskChatInputProps = {
  handleUserSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedAgent: SelectedAgent,
    fileReference?: string,
    modelId?: string,
    createPullRequest?: boolean,
  ) => Promise<void>
  initialRepo?: Repository | null
  initialReference?: CreateTaskReference | null
  hideRepoSelector?: boolean
  isInPortal?: ChatInputProps['isInPortal']
  textAreaRef?: ChatInputProps['textAreaRef']
  size?: 'small' | 'large'
  className?: string
  eventType?: string
  shouldClearTextOnSubmit?: boolean
  initialText?: string
}

export function NewTaskChatInput({
  handleUserSubmit,
  initialRepo,
  initialReference,
  hideRepoSelector,
  isInPortal,
  textAreaRef,
  size = 'large',
  className,
  eventType,
  shouldClearTextOnSubmit = true,
  initialText,
}: NewTaskChatInputProps) {
  // Use the chat manager to clear references when a new task is started
  const manager = useChatManager()
  const {plan} = useEntitlement()
  const {models: availableModels} = useAgentModels()

  const [selectedModelId, setSelectedModelId] = useLatestAgentModel()

  const [lastUsedRepo, setLastUsedRepo] = useLastUsedRepository()
  const [selectedRepo, setSelectedRepo] = useState<DropdownRepository | null>(null)
  const [ccaDisabled, setCCADisabled] = useState(false)
  const isMemberRequestAgentPanelEnabled = isFeatureEnabled('swe_agent_member_requests_agent_panel')
  const [referenceRepo, setReferenceRepo] = useState<CopilotChatRepo | null>(null)
  const [selectedBranch, setSelectedBranch] = useState<string>('')
  const [selectedAgent, setSelectedAgent] = useLastSelectedAgent()

  const {data: agentInfo} = useAgentInfo(selectedAgent.topLevelAgentId ? Number(selectedAgent.topLevelAgentId) : null)

  const shouldShowModelPicker =
    copilotFeatureFlags.codingAgentModelSelection &&
    (copilotFeatureFlags.codingAgentModelSelectionAllSkus ||
      plan === CopilotPlan.IndividualPro ||
      plan === CopilotPlan.IndividualProPlus) &&
    isCopilotOrCustomAgent(selectedAgent, agentInfo?.slug)

  const [reference, setReference] = useState<CreateTaskReference | null>(initialReference || null)
  const [createPullRequest, setCreatePullRequest] = useState<boolean>(true)
  const [searchParams] = useSearchParams()
  const branchParam = searchParams.get('branch')

  const isSubAgentsEnabled = isFeatureEnabled('copilot_swe_agent_use_subagents')
  const isValidRepo = initialRepo && Object.keys(initialRepo).length > 0
  const nwoParam = searchParams.get('repo')
  let initialNameWithOwner = ''
  if (isValidRepo) {
    initialNameWithOwner = `${initialRepo.ownerLogin}/${initialRepo.name}`
  } else if (nwoParam) {
    initialNameWithOwner = nwoParam
  } else if (lastUsedRepo) {
    initialNameWithOwner = `${lastUsedRepo.ownerLogin}/${lastUsedRepo.name}`
  }

  const onSubmit = useCallback(
    async (content: string) => {
      sendEvent('agent_sessions.task_view', {
        target: 'NEW_TASK_SUBMIT',
        defaultToTask: copilotFeatureFlags.defaultToTaskInput,
        defaultToTaskControl: copilotFeatureFlags.defaultToTaskControl,
        askModeDropdown: copilotFeatureFlags.askModeDropdown,
      })

      const referenceName =
        referenceRepo && reference
          ? getReferenceName(makeFileOrSnippetReference({referenceRepo, reference}))
          : undefined

      // Save the selected repository to localStorage when task is created
      if (selectedRepo?.id != null) {
        const result = await manager.service.fetchRepo(selectedRepo.id)
        if (result.ok && result.payload) {
          setLastUsedRepo(result.payload)
        }
      }

      const modelIdToUse =
        selectedModelId ?? availableModels?.find(m => m.is_chat_default)?.id ?? availableModels?.[0]?.id
      return handleUserSubmit(
        content,
        selectedRepo,
        selectedBranch,
        selectedAgent,
        referenceName,
        modelIdToUse,
        createPullRequest,
      )
    },
    [
      handleUserSubmit,
      selectedRepo,
      selectedBranch,
      selectedAgent,
      reference,
      referenceRepo,
      manager.service,
      setLastUsedRepo,
      availableModels,
      selectedModelId,
      createPullRequest,
    ],
  )

  const handleRepoSelect = useCallback(
    (repo: DropdownRepository | undefined) => {
      if (!repo) return

      setSelectedRepo(repo)
      setSelectedBranch(repo.defaultBranch || '')
      setCCADisabled(!repo.enabled && isMemberRequestAgentPanelEnabled)
    },
    [isMemberRequestAgentPanelEnabled],
  )

  // Fetch the reference repo
  useEffect(() => {
    if (initialRepo?.id != null) {
      ;(async () => {
        const repoResponse = await manager.service.fetchRepo(initialRepo.id)
        if (repoResponse.payload) setReferenceRepo(repoResponse.payload)
      })()
    }
  }, [initialRepo, manager])

  useEffect(() => {
    // If the repo selector is hidden and we have an initial repo NWO, try to auto-select it.
    if (hideRepoSelector && initialNameWithOwner) {
      const fetchInitialRepo = async () => {
        try {
          const repositories = await queryForRepos(initialNameWithOwner)
          const repo = repositories.find(
            (repoData: {nameWithOwner: string}) => repoData.nameWithOwner === initialNameWithOwner,
          )
          if (repo && repo.enabled) {
            setSelectedRepo(repo)
            setSelectedBranch(branchParam ?? repo.defaultBranch)
            setCCADisabled(false)
            return
          }
          if (repo && isMemberRequestAgentPanelEnabled) {
            setCCADisabled(true)
          }
        } catch {
          // Error querying for specific repo
        }
      }
      fetchInitialRepo()
    }
    // We only want this hook to run once since it's fetching the initial repo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      <ChatInput
        initialText={initialText}
        className={className}
        modes={[
          {
            name: ModeSupportName.Task,
            icon: AgentIcon,
            submitLabel: 'Start task',
            placeholder: 'Describe a coding task to work on',
            textAreaAriaLabel: 'Coding task prompt',
            supportsAttachments: false,
            // Setting this to false because the references aren't properly included in the problem statement sent to CCA.
            supportsReferenceAttachments: false,
            supportsModelSwitching: shouldShowModelPicker,
          },
        ]}
        size={size}
        onSubmit={onSubmit}
        initialReference={
          referenceRepo && reference ? makeFileOrSnippetReference({reference, referenceRepo}) : undefined
        }
        extraToolbar={
          <RepoBranchAgentPicker
            hideRepoSelector={hideRepoSelector}
            initialRepoNWO={initialNameWithOwner}
            initialBranch={branchParam || undefined}
            selectedRepo={selectedRepo || undefined}
            selectedBranch={selectedBranch}
            selectedAgent={selectedAgent}
            onRepoSelect={handleRepoSelect}
            onBranchSelect={(branch: string | undefined) => setSelectedBranch(branch || '')}
            onAgentSelect={(agent: SelectedAgent) => setSelectedAgent(agent)}
            agentSelectionEnabled={isSubAgentsEnabled && !ccaDisabled}
          />
        }
        submitDisabled={!selectedRepo || ccaDisabled}
        onRemoveReference={() => {
          setReference(null)
        }}
        isInPortal={isInPortal}
        textAreaRef={textAreaRef}
        // Intentionally passing an empty function to onNewThreadSelected to enable the model picker UI.
        // ChatInput requires this prop to show the model picker, even if thread creation is not needed here.
        onNewThreadSelected={async () => {}}
        customModelPicker={
          shouldShowModelPicker
            ? () => <ModelPicker selectedModelId={selectedModelId} onUpdateModel={setSelectedModelId} />
            : undefined
        }
        storageKeySuffix={eventType}
        showStartPullRequestButton={copilotFeatureFlags.defaultToTaskInput}
        shouldClearTextOnSubmit={shouldClearTextOnSubmit}
      />
      {ccaDisabled && selectedRepo && isMemberRequestAgentPanelEnabled && (
        <MemberRequestBanner
          repo={selectedRepo}
          onRepoUpdate={async () => {
            // Re-fetch the repository data to get updated CCA flags
            const nameWithOwner = selectedRepo.nameWithOwner
            const repos = await queryForRepos(nameWithOwner)
            const updatedRepo = repos.find(r => r.nameWithOwner === nameWithOwner)
            if (updatedRepo) {
              setSelectedRepo(updatedRepo)
              setCCADisabled(!updatedRepo.enabled && isMemberRequestAgentPanelEnabled)
            }
          }}
        />
      )}

      {copilotFeatureFlags.codingAgentPullRequestToggle && (
        <div className="p-3">
          <span id="create-pull-request-toggle-label" className="mr-3">
            Create a pull request
          </span>
          <ToggleSwitch
            size="small"
            aria-labelledby="create-pull-request-toggle-label"
            checked={createPullRequest}
            onClick={() => setCreatePullRequest(!createPullRequest)}
            statusLabelPosition="end"
          />
        </div>
      )}
    </>
  )
}

try{ NewTaskChatInput.displayName ||= 'NewTaskChatInput' } catch {}